import {
  deriveWorkflowTriggerConditionFields,
  PLATFORM_EVENT_DEFINITIONS,
  PLATFORM_EVENTS,
} from "./platform-events";
import { WEBHOOK_EVENT_TYPES, type WebhookEventType } from "./webhook-events";
import type {
  ObjectChangeTriggerDelivery,
  WorkflowTriggerConditionField,
  WorkflowTriggerConditionOperator,
  WorkflowTriggerEventMetadata,
} from "./workflow-trigger-definition";

export * from "./workflow-trigger-definition";

type RegisteredWorkflowMetadata = {
  [EventType in keyof typeof PLATFORM_EVENT_DEFINITIONS]: (typeof PLATFORM_EVENT_DEFINITIONS)[EventType] extends {
    workflowTrigger: infer Metadata;
  }
    ? Metadata
    : never;
}[keyof typeof PLATFORM_EVENT_DEFINITIONS];

type ExplicitWorkflowEventType = {
  [EventType in keyof typeof PLATFORM_EVENT_DEFINITIONS]: (typeof PLATFORM_EVENT_DEFINITIONS)[EventType] extends {
    workflowTrigger: unknown;
  }
    ? EventType
    : never;
}[keyof typeof PLATFORM_EVENT_DEFINITIONS];

type GenericPlatformEventType = Exclude<WebhookEventType, ExplicitWorkflowEventType>;
export type GenericPlatformEventTriggerType = `platform_event:${GenericPlatformEventType}`;
export type EventBackedTriggerType =
  | RegisteredWorkflowMetadata["triggerType"]
  | GenericPlatformEventTriggerType;

export type WorkflowEventTriggerDefinition = WorkflowTriggerEventMetadata & {
  eventTypes: readonly string[];
  conditionFields: Readonly<Record<string, WorkflowTriggerConditionField>>;
  focused: boolean;
};

export type ObjectChangeTriggerDefinition = WorkflowEventTriggerDefinition & {
  delivery: ObjectChangeTriggerDelivery;
};

export function isObjectChangeTriggerDefinition(
  definition: WorkflowEventTriggerDefinition,
): definition is ObjectChangeTriggerDefinition {
  return definition.delivery.kind === "object_change";
}

function isWebhookEventType(eventType: string): eventType is WebhookEventType {
  return (WEBHOOK_EVENT_TYPES as readonly string[]).includes(eventType);
}

function genericEventMetadata(eventType: WebhookEventType): WorkflowTriggerEventMetadata {
  const label = humanizePlatformEventType(eventType);
  return {
    triggerType: `platform_event:${eventType}`,
    label,
    icon: "event",
    delivery: {
      kind: "object_change",
      resourceIdInput: "resourceId",
      statusLine: `Queued from the ${label.toLowerCase()} event.`,
    },
  };
}

function buildWorkflowTriggerCatalog() {
  const catalog = {} as Record<EventBackedTriggerType, WorkflowEventTriggerDefinition>;
  for (const [eventType, definition] of Object.entries(PLATFORM_EVENT_DEFINITIONS)) {
    const explicitMetadata =
      "workflowTrigger" in definition
        ? (definition.workflowTrigger as RegisteredWorkflowMetadata)
        : undefined;
    let metadata: WorkflowTriggerEventMetadata;
    let focused: boolean;
    if (explicitMetadata) {
      metadata = explicitMetadata;
      focused = "focused" in explicitMetadata ? (explicitMetadata.focused ?? true) : true;
    } else {
      if (!isWebhookEventType(eventType)) continue;
      metadata = genericEventMetadata(eventType);
      focused = false;
    }

    const triggerType = metadata.triggerType as EventBackedTriggerType;
    const existing = catalog[triggerType];
    if (existing) {
      existing.eventTypes = [...existing.eventTypes, eventType];
      continue;
    }
    catalog[triggerType] = {
      ...metadata,
      eventTypes: [eventType],
      conditionFields: {
        ...(explicitMetadata || "stateAfter" in definition
          ? {}
          : deriveWorkflowTriggerConditionFields(definition.data)),
        ...definition.conditionFields,
      },
      focused,
    };
  }
  return catalog;
}

/** Derived workflow view of the platform-event registry. */
export const WORKFLOW_TRIGGER_CATALOG = buildWorkflowTriggerCatalog();

export function humanizePlatformEventType(eventType: string): string {
  const words = eventType.replaceAll(".", " ").replaceAll("_", " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export type BusTriggerEvent =
  (typeof WORKFLOW_TRIGGER_CATALOG)[EventBackedTriggerType]["eventTypes"][number];

export function workflowTriggerConditionOperators(
  type: EventBackedTriggerType,
): Readonly<Record<string, readonly WorkflowTriggerConditionOperator[]>> {
  return Object.fromEntries(
    Object.entries(WORKFLOW_TRIGGER_CATALOG[type].conditionFields).map(([field, definition]) => [
      field,
      definition.operators,
    ]),
  );
}

export function workflowTriggerTypeForEvent(eventType: string): EventBackedTriggerType | null {
  const definition = PLATFORM_EVENTS[eventType as keyof typeof PLATFORM_EVENTS];
  if (!definition) return null;
  if ("workflowTrigger" in definition && definition.workflowTrigger) {
    return definition.workflowTrigger.triggerType as EventBackedTriggerType;
  }
  if (!isWebhookEventType(eventType)) return null;
  return `platform_event:${eventType}`;
}
